/**
 * Handle Vite chunk preload errors by reloading the page once.
 * 
 * When a new deploy happens with new hashed asset filenames, old pages with
 * cached JS may try to dynamically import chunks (CSS or JS) with old hashes
 * that no longer exist. This causes "Unable to preload CSS for /vite-dev/assets/..."
 * errors. The standard solution is to reload the page once to fetch fresh HTML
 * with the new asset references.
 * 
 * Safeguards:
 * - Uses sessionStorage to track reload attempts and prevent infinite loops
 * - Only triggers on preload/chunk errors
 * - Timestamp expires after 10 seconds to prevent stale flags from blocking future reloads
 */

const RELOAD_FLAG_KEY = 'vite-preload-error-reload-attempted'
const RELOAD_FLAG_TIMEOUT_MS = 10000 // 10 seconds

/**
 * Check if an error is a chunk load error.
 * Matches errors from:
 * - Vite 5.4+ dynamic import failures
 * - Browser module script loading failures
 * - CSS preload failures
 */
export function isChunkLoadError(err) {
  if (!err) return false
  
  const message = String(err.message || err)
  
  return (
    message.includes('Failed to fetch dynamically imported module') ||
    message.includes('Importing a module script failed') ||
    message.includes('error loading dynamically imported module') ||
    message.includes('Unable to preload CSS')
  )
}

/**
 * Reload the page once if this is a chunk load error and we haven't reloaded recently.
 * Returns true if a reload was triggered.
 */
export function reloadOnceForChunkError(err) {
  if (!isChunkLoadError(err)) {
    return false
  }

  if (typeof window === 'undefined' || typeof sessionStorage === 'undefined') {
    return false
  }

  const flagValue = sessionStorage.getItem(RELOAD_FLAG_KEY)
  const now = Date.now()
  
  // Check if we've recently attempted a reload
  if (flagValue) {
    const timestamp = parseInt(flagValue, 10)
    if (!isNaN(timestamp) && (now - timestamp) < RELOAD_FLAG_TIMEOUT_MS) {
      console.error('[Chunk load error] Reload already attempted recently. Asset might be genuinely missing.')
      return false
    }
  }

  // Attempt reload
  console.info('[Chunk load error] Reloading page to fetch fresh assets...')
  sessionStorage.setItem(RELOAD_FLAG_KEY, String(now))
  window.location.reload()
  return true
}

/**
 * Wrap a React.lazy import to reload on chunk load errors.
 * 
 * Use this instead of bare React.lazy() to handle stale chunk errors
 * that don't fire vite:preloadError (e.g., on Vite 5.4+).
 * 
 * @example
 * const LegacyLogin = lazyWithReload(() => import('./legacy_app/LegacyLogin'))
 */
export function lazyWithReload(importFn) {
  if (typeof window === 'undefined') {
    // SSR: return a plain lazy loader
    return typeof React !== 'undefined' ? React.lazy(importFn) : importFn
  }

  return typeof React !== 'undefined'
    ? React.lazy(async () => {
        try {
          return await importFn()
        } catch (err) {
          // Attempt reload if this is a chunk error
          if (reloadOnceForChunkError(err)) {
            // Reload triggered; throw to prevent render attempts
            throw err
          }
          // Not a chunk error or already reloaded; let it propagate
          throw err
        }
      })
    : importFn
}

/**
 * Install the vite:preloadError event handler.
 * Call this once per entrypoint at the top level.
 */
export function installVitePreloadErrorHandler() {
  if (typeof window === 'undefined') return

  // Handle Vite preload errors (fires for CSS preload errors and some import errors)
  window.addEventListener('vite:preloadError', (event) => {
    console.warn('[Vite preload error]', event.payload)
    reloadOnceForChunkError(event.payload)
  })
}
